import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { authApi } from '../api/endpoints';

/**
 * Nút "Sign in with Google" của Google Identity Services.
 *
 * Script GIS được nạp động (không nhét <script> vào index.html) vì Client ID
 * lấy từ API — khi Admin tắt tính năng thì trang đăng nhập không nên tải thêm
 * script bên thứ ba nào cả.
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (res: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** Nạp script GIS đúng MỘT lần cho cả vòng đời trang. */
function loadGisScript(): Promise<void> {
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
  if (existing) {
    return existing.dataset.loaded === '1'
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Không tải được Google script')));
        });
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GIS_SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener('load', () => {
      el.dataset.loaded = '1';
      resolve();
    });
    el.addEventListener('error', () => reject(new Error('Không tải được Google script')));
    document.head.appendChild(el);
  });
}

interface Props {
  onCredential: (credential: string) => void;
}

export function GoogleSignInButton({ onCredential }: Props): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  // Giữ callback trong ref: GIS chỉ nhận callback lúc initialize, nếu đóng gói
  // giá trị cũ thì lần đăng nhập thứ hai sẽ gọi vào closure hết hạn.
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await authApi.googleConfig();
        if (cancelled || !cfg.enabled || !cfg.client_id) return;
        await loadGisScript();
        if (cancelled || !containerRef.current || !window.google) return;

        window.google.accounts.id.initialize({
          client_id: cfg.client_id,
          callback: (res) => callbackRef.current(res.credential),
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'signin_with',
          locale: 'vi',
        });
        setReady(true);
      } catch {
        // Google tắt hoặc mạng hỏng → chỉ ẩn nút, form mật khẩu vẫn dùng được.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ display: ready ? 'block' : 'none' }} data-testid="google-signin-wrapper">
      <Divider sx={{ my: 2 }}>
        <Typography variant="caption" color="text.secondary">
          hoặc
        </Typography>
      </Divider>
      <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center' }} />
    </Box>
  );
}
